import {
  Agent,
  EXTERNAL_AGENTS,
  EXTERNAL_MODELS,
  Model,
  isValidAgent,
  isValidModel,
} from '@/features/prince/models';
import { Bot, ChevronDown, Cpu } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

interface PrinceChatHeaderProps {
  onAgentChange: (agent: Agent) => { newConversationId: string };
  isScoutInternalAgentsEnabled: boolean;
  isScoutInternalModelsEnabled: boolean;
}
export function PrinceChatHeader({
  onAgentChange,
  isScoutInternalAgentsEnabled,
  isScoutInternalModelsEnabled,
}: PrinceChatHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('prince');
  const [searchParams] = useSearchParams();
  const { agent: agentParam, model: modelParam } = useParams<{ agent?: string; model?: string }>();

  const showModelSelector = searchParams.get('header') === 'true';
  const showAgentSelector = searchParams.get('header') === 'true' || isScoutInternalAgentsEnabled;

  if (!showModelSelector && !showAgentSelector) {
    return null;
  }

  const currentAgent: Agent =
    agentParam && isValidAgent(agentParam) ? (agentParam as Agent) : Agent.Sitetracker;
  const currentModel: Model =
    modelParam && isValidModel(modelParam) ? (modelParam as Model) : Model.Claude35HaikuLatest;

  // All available agents (matching your cleaned up enum)
  const availableAgents: { value: Agent; label: string; description: string }[] = [
    {
      value: Agent.Chatbot,
      label: t('agents.chatbot.name'),
      description: t('agents.chatbot.description'),
    },
    {
      value: Agent.ResearchSpecialist,
      label: t('agents.researchSpecialist.name'),
      description: t('agents.researchSpecialist.description'),
    },
    {
      value: Agent.Sitetracker,
      label: t('agents.sitetracker.name'),
      description: t('agents.sitetracker.description'),
    },
    {
      value: Agent.Meetings,
      label: t('agents.meetings.name'),
      description: t('agents.meetings.description'),
    },
    {
      value: Agent.Knowledge,
      label: t('agents.knowledge.name'),
      description: t('agents.knowledge.description'),
    },
    {
      value: Agent.CommunityKnowledge,
      label: t('agents.community_knowledge.name'),
      description: t('agents.community_knowledge.description'),
    },
    {
      value: Agent.InternalSitetracker,
      label: t('agents.internalSitetracker.name'),
      description: t('agents.internalSitetracker.description'),
    },
    {
      value: Agent.InternalSupervisor,
      label: t('agents.internalSupervisor.name'),
      description: t('agents.internalSupervisor.description'),
    },
    {
      value: Agent.Classification,
      label: t('agents.classification.name'),
      description: t('agents.classification.description'),
    },
    {
      value: Agent.Sthr,
      label: t('agents.sthr.name'),
      description: t('agents.sthr.description'),
    },
  ].filter(agent => isScoutInternalAgentsEnabled || EXTERNAL_AGENTS.includes(agent.value));

  const availableModels: { value: Model; label: string; category: string }[] = [
    {
      value: Model.ClaudeOpus4120250805,
      label: t('models.labels.claudeOpus41'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.ClaudeOpus420250514,
      label: t('models.labels.claudeOpus4'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.ClaudeSonnet4520250929,
      label: t('models.labels.claudeSonnet45'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.ClaudeSonnet420250514,
      label: t('models.labels.claudeSonnet4'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Claude37SonnetLatest,
      label: t('models.labels.claude37SonnetLatest'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Claude37Sonnet20250219,
      label: t('models.labels.claude37Sonnet'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Claude35SonnetLatest,
      label: t('models.labels.claude35SonnetLatest'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Claude35Sonnet20241022,
      label: t('models.labels.claude35Sonnet20241022'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Claude35Sonnet20240620,
      label: t('models.labels.claude35Sonnet20240620'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.ClaudeHaiku4520251001,
      label: t('models.labels.claudeHaiku45'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Claude35HaikuLatest,
      label: t('models.labels.claude35HaikuLatest'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Claude35Haiku20241022,
      label: t('models.labels.claude35Haiku20241022'),
      category: t('models.categories.anthropic'),
    },
    {
      value: Model.Gpt5,
      label: t('models.labels.gpt5'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.Gpt4o,
      label: t('models.labels.gpt4o'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.Gpt4oMini,
      label: t('models.labels.gpt4oMini'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.Gpt4Turbo,
      label: t('models.labels.gpt4Turbo'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.Gpt4TurboPreview,
      label: t('models.labels.gpt4TurboPreview'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.Gpt4,
      label: t('models.labels.gpt4'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.Gpt35Turbo,
      label: t('models.labels.gpt35Turbo'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.O1Preview,
      label: t('models.labels.o1Preview'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.O1Mini,
      label: t('models.labels.o1Mini'),
      category: t('models.categories.openai'),
    },
    {
      value: Model.Gemini25Pro,
      label: t('models.labels.gemini25Pro'),
      category: t('models.categories.gemini'),
    },
    {
      value: Model.Gemini25Flash,
      label: t('models.labels.gemini25Flash'),
      category: t('models.categories.gemini'),
    },
    {
      value: Model.Gemini20Flash,
      label: t('models.labels.gemini20Flash'),
      category: t('models.categories.gemini'),
    },
    {
      value: Model.Gemini15Pro,
      label: t('models.labels.gemini15Pro'),
      category: t('models.categories.gemini'),
    },
    {
      value: Model.Gemini15Flash,
      label: t('models.labels.gemini15Flash'),
      category: t('models.categories.gemini'),
    },
    {
      value: Model.Gemini15ProLatest,
      label: t('models.labels.gemini15ProLatest'),
      category: t('models.categories.gemini'),
    },
    {
      value: Model.Gemini15FlashLatest,
      label: t('models.labels.gemini15FlashLatest'),
      category: t('models.categories.gemini'),
    },
  ].filter(model => isScoutInternalModelsEnabled || EXTERNAL_MODELS.includes(model.value));

  const handleAgentChange = (newAgent: Agent) => {
    const { newConversationId } = onAgentChange(newAgent);

    const params = new URLSearchParams({
      ...Object.fromEntries(searchParams.entries()),
      conversation: newConversationId,
    });

    navigate(`/prince/${newAgent}/${currentModel}?${params.toString()}`);
  };

  const handleModelChange = (newModel: Model) => {
    navigate(
      `/prince/${currentAgent}/${newModel}${searchParams.size > 0 ? `?${searchParams.toString()}` : ''}`
    );
  };

  const currentAgentInfo = availableAgents.find(a => a.value === currentAgent);
  const currentModelInfo = availableModels.find(m => m.value === currentModel);

  // Group models by category for organized dropdown
  const modelsByCategory = availableModels.reduce(
    (acc, model) => {
      if (!acc[model.category]) {
        acc[model.category] = [];
      }
      acc[model.category].push(model);
      return acc;
    },
    {} as Record<string, typeof availableModels>
  );

  return (
    <div className="border-b border-gray-200 px-4 py-3 prince-chat-header">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Agent Selector */}
          {showAgentSelector && (
            <div className="flex items-center space-x-2">
              <Bot className="h-4 w-4 text-gray-500" />
              <div className="relative">
                <select
                  value={currentAgent}
                  onChange={e => handleAgentChange(e.target.value as Agent)}
                  aria-label={t('selectors.agent')}
                  className="appearance-none bg-white border border-gray-300 rounded-md px-3 py-1.5 pr-8 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="agent-selector"
                >
                  {availableAgents.map(agent => (
                    <option key={agent.value} value={agent.value}>
                      {agent.label}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
          )}

          {/* Model Selector */}
          {showModelSelector && (
            <div className="flex items-center space-x-2">
              <Cpu className="h-4 w-4 text-gray-500" />
              <div className="relative">
                <select
                  value={currentModel}
                  onChange={e => handleModelChange(e.target.value as Model)}
                  aria-label={t('selectors.model')}
                  className="appearance-none bg-white border border-gray-300 rounded-md px-3 py-1.5 pr-8 text-sm font-medium text-gray-900 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="model-selector"
                >
                  {Object.entries(modelsByCategory).map(([category, models]) => (
                    <optgroup key={category} label={category}>
                      {models.map(model => (
                        <option key={model.value} value={model.value}>
                          {model.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                </div>
              </div>
            </div>
          )}
        </div>
        {/* Current Selection Info */}
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            {currentAgentInfo?.label || currentAgent}
          </div>
          <div className="text-xs text-gray-500">{currentModelInfo?.label || currentModel}</div>
        </div>
      </div>

      {/* Agent Description */}
      {currentAgentInfo?.description && (
        <div className="mt-2 text-xs text-gray-600">{currentAgentInfo.description}</div>
      )}
    </div>
  );
}
